import { ArrowUpRight, ExternalLink, Newspaper } from 'lucide-react';
import { apiPaths, type NewsResponse } from '../../../shared/api/contract.ts';
import { DEFAULT_CITY } from '../../../shared/cities/index.ts';
import { FRESHNESS_POLICIES } from '../../../shared/freshness/index.ts';
import { SectionCard } from '../../components/SectionCard.tsx';
import { SkeletonRows } from '../../components/Skeleton.tsx';
import { useApi } from '../../hooks/useApi.ts';
import { formatAge, formatDay } from '../../lib/format.ts';

/** Page officielle des actualités, vers laquelle renvoie la carte. */
const NEWS_PAGE = 'https://www.ermont.fr/2252/actualites.htm';

const LIMIT = 4;

/**
 * Actualités de la Ville d'Ermont : titre, ancienneté et lien vers la fiche officielle.
 * Aucun texte ni image repris (docs/02 §4) : le trafic va à l'éditeur.
 */
export function LocalNews() {
  const news = useApi<NewsResponse>(apiPaths.news(DEFAULT_CITY.slug), FRESHNESS_POLICIES.news.refreshMs);
  return (
    <SectionCard
      title="Actualités locales"
      icon={<Newspaper size={22} aria-hidden="true" />}
      kind="news"
      subject="d’actualités"
      section={news.data?.news}
      loading={news.loading}
      fetchError={news.error}
      skeleton={<SkeletonRows rows={3} badge="round" />}
      actions={
        <a className="text-link" href={NEWS_PAGE} rel="noopener noreferrer" target="_blank">
          Voir toutes les actus <ExternalLink size={14} aria-hidden="true" />
          <span className="visually-hidden"> (nouvel onglet)</span>
        </a>
      }
    >
      {(items, now) =>
        items.length === 0 ? (
          <div className="empty-state">
            <Newspaper size={28} aria-hidden="true" />
            <p>Aucune actualité publiée pour l’instant.</p>
          </div>
        ) : (
          <ul className="news-list">
            {items.slice(0, LIMIT).map((item) => (
              <li key={item.id}>
                <a href={item.url} rel="noopener noreferrer" target="_blank">
                  {item.title}
                  <ArrowUpRight size={16} aria-hidden="true" />
                  <span className="visually-hidden"> (nouvel onglet)</span>
                </a>
                <time dateTime={item.publishedAt} title={formatDay(item.publishedAt)}>
                  {formatAge(now.getTime() - Date.parse(item.publishedAt))}
                </time>
              </li>
            ))}
          </ul>
        )
      }
    </SectionCard>
  );
}
